import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import TenantUserList from '@/components/admin/TenantManagement/TenantUserList';
import InviteUserModal from '@/components/admin/TenantManagement/InviteUserModal';
import {
  listTenantUsers,
  resendTenantUserInvite,
  setTenantUserAppScope,
} from '@/components/admin/TenantManagement/api';
import EditAppScopeModal from '@/components/admin/TenantManagement/EditAppScopeModal';
import { fetchTenantSidebarConfig } from '@/lib/tenantSidebarApi';

/**
 * Phase 2G.2 — the tenant-facing Users page.
 *
 * ## Why a new page rather than exposing `/admin/users`
 *
 * The 21 Aug baseline sync put six keys on every `tenant_admin` copy:
 * `roles.view`, `roles.manage`, `users.view`, `users.invite`, `users.edit`,
 * `users.assign_role`. 2G gave the first two a home. The user keys had
 * none, so 22 production role copies carried them with nowhere to use them.
 *
 * `/admin/users` is the root User & Resource Management screen —
 * contractors, resource types, `is_assignable_as_*` flags, contractor rate
 * approval. Almost none of it means anything to a customer, and several of
 * its panels read SimpliMeta-internal collections. Reusing it the way 2G
 * reused `/admin/roles` was not an option: there the backend already
 * returned the right thing per caller, so one component served both. Here
 * the two audiences want different screens.
 *
 * What they do share is `TenantUserList`, which has been presentation-only
 * since 1D.8 and is imported here unchanged.
 *
 * ## Route naming
 *
 * `/admin/tenant-users`, keeping the prefix consistent with the Roles entry
 * sitting beside it in the same nav group, and staying clearly distinct
 * from `/admin/users`. The prefix is cosmetically odd for a tenant — 2G
 * made the same trade for the same reason.
 *
 * ## What a tenant admin can and cannot do here
 *
 * Invite, view and resend. NOT delete: `DELETE /api/admin/users/{id}` is
 * gated on `users.delete`, which is deliberately absent from the
 * `tenant_admin` baseline (tests/test_tenant_user_management_2d2.py asserts
 * it), and `allowed_permission_keys` stops a tenant authoring a role that
 * carries it. The Delete action is therefore gated on the key rather than
 * shown and left to 403. That leaves a real gap — a tenant admin who
 * invites a wrong address cannot clear it and must wait out the 7-day
 * expiry or raise a ticket. Widening the baseline is a product decision for
 * Ryan, not one to make by adding a button.
 *
 * Editing an existing user is likewise absent. `users.edit` and
 * `users.assign_role` are both on the baseline, but `TenantUserList` has
 * never had an edit affordance, and building one means a role-change UI
 * with its own escalation surface. Out of scope here; the keys stay
 * unreachable from this screen for now.
 *
 * ## Role options come from the server
 *
 * `InviteUserModal` defaults to a hardcoded `tenant_admin` / `tenant_member`
 * pair, which is right for the root path in TenantDetail. It is wrong here:
 * 2G.1 lets a tenant admin clone a role, and `assignable_role_names` accepts
 * any role the tenant owns — so a cloned role was authorable but not
 * assignable, which made the clone feature decorative. This page passes the
 * tenant's own roles in as `roleOptions`.
 *
 * `GET /api/roles?scope=manageable` returns exactly `{tenant_id: mine}`,
 * which for a tenant is the same set `assignable_role_names` computes:
 * their own roles, which since 2B.1 already include their `tenant_admin`
 * and `tenant_member` copies. Matching sets rather than a second definition
 * of the same rule — if they ever diverge, the server rejects and the 400
 * names what is allowed.
 */
export default function TenantUsersPage() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const tenantId = user?.tenant_id;

  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apps, setApps] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [scopeTarget, setScopeTarget] = useState(null);
  const [resendingUserId, setResendingUserId] = useState('');

  const canInvite = hasPermission('users.invite');
  // 2I.3c — the same key the endpoint requires. Gated on the key rather
  // than shown and left to 403, matching how Delete is handled here.
  const canEditUsers = hasPermission('users.edit');

  const loadUsers = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      setError('No tenant is associated with your account.');
      return;
    }
    setError('');
    try {
      const res = await listTenantUsers(tenantId);
      if (res.ok) {
        const data = await res.json();
        // The endpoint returns a bare array; the guard matches the one in
        // TenantDetail so a future backend normalisation to { users: [...] }
        // doesn't silently empty the page.
        setUsers(Array.isArray(data) ? data : data?.users || []);
      } else if (res.status === 403) {
        setError('You do not have access to this tenant\u2019s users.');
      } else {
        setError('Could not load users. Try again.');
      }
    } catch {
      setError('Network error loading users.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Assignable roles for the invite picker. Failure is non-fatal: leaving
  // roleOptions null makes InviteUserModal fall back to its built-in
  // tenant_admin / tenant_member pair, so the invite flow still works
  // without the tenant's authored roles.
  useEffect(() => {
    if (!canInvite) return;
    let cancelled = false;
    api
      .get(`${API_URL}/api/roles?scope=manageable`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        const options = data
          .filter((r) => r?.name)
          .map((r) => ({
            value: r.name,
            label: r.display_name || r.name,
            description: r.description || '',
          }));
        if (options.length > 0) setRoleOptions(options);
      })
      .catch(() => {
        /* non-fatal — modal falls back to its defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [canInvite]);

  // 2I.3b — the apps this admin may see, which is exactly the set they may
  // put in someone else's scope.
  //
  // sidebar-config rather than a new endpoint: it is already narrowed by
  // the tenant's active grants, by the caller's own `app_scope` (slice 1)
  // and by the caller's permission keys, so "never wider than your own"
  // falls out of the data source instead of being re-derived here. A
  // second definition of that rule is a second thing to keep in step, and
  // `_resolve_invitee_scope` refuses a wider request regardless.
  //
  // Doubles as the id -> name map for the Apps column, so a scope reads
  // "MATCH" rather than "app_a984d000d748".
  //
  // Non-fatal on failure: the helper resolves to `{ apps: [] }` rather
  // than throwing, which hides the column and the picker and leaves the
  // invite flow working exactly as it did before this slice.
  useEffect(() => {
    let cancelled = false;
    fetchTenantSidebarConfig().then((cfg) => {
      if (!cancelled) setApps(cfg.apps || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const appLabels = React.useMemo(
    () =>
      apps.reduce((acc, a) => {
        if (a?.app_id) acc[a.app_id] = a.name || a.app_id;
        return acc;
      }, {}),
    [apps],
  );

  // The modal surfaces the thrown message, so the server's own 400 —
  // which names the caller's access and what they asked for — reaches the
  // person rather than being flattened into "could not save".
  const handleSaveAppScope = useCallback(
    async (targetUserId, appScope) => {
      const res = await setTenantUserAppScope(targetUserId, appScope);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail =
          typeof body?.detail === 'string' ? body.detail : body?.detail?.message;
        if (res.status === 404) {
          // Same answer for "gone" and "outside your access" — the
          // endpoint deliberately does not distinguish them, and neither
          // should the copy.
          throw new Error('That user is no longer in your workspace.');
        }
        throw new Error(detail || 'Could not update app access.');
      }
      toast.success('App access updated.');
      setScopeTarget(null);
      loadUsers();
    },
    [loadUsers],
  );

  const handleResendInvite = useCallback(
    async (target) => {
      setResendingUserId(target.user_id);
      try {
        const res = await resendTenantUserInvite(target.user_id);
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          toast.success(`Invite resent to ${target.name || target.email}`);
          // Email delivery is not reliable on every deployment, so the
          // link is copied as a fallback channel — same rationale as the
          // root path in TenantDetail.
          if (data.invite_link) {
            try {
              await navigator.clipboard.writeText(data.invite_link);
              toast.info('New invite link copied to clipboard');
            } catch {
              /* clipboard blocked (iframe / permissions) — non-fatal */
            }
          }
          loadUsers();
        } else if (res.status === 400) {
          toast.error('This person has already accepted their invite.');
          loadUsers();
        } else if (res.status === 403) {
          toast.error('You do not have permission to resend invites.');
        } else if (res.status === 404) {
          // Slice A: a target outside the caller's tenant answers exactly
          // as a deleted one does. Refetching resolves the common case.
          toast.error('That user is no longer in your tenant.');
          loadUsers();
        } else {
          toast.error('Could not resend the invite. Try again.');
        }
      } catch (err) {
        toast.error(err?.message || 'Could not resend the invite.');
      } finally {
        setResendingUserId('');
      }
    },
    [loadUsers],
  );

  return (
    <div data-testid="tenant-users-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-boss-text">Users</h1>
          <p className="text-sm text-boss-text-secondary mt-1">
            People who can sign in to your workspace.
          </p>
        </div>
        {canInvite && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="btn-primary inline-flex items-center gap-1.5 shrink-0"
            data-testid="tenant-users-invite-button"
          >
            <Plus size={14} />
            Invite user
          </button>
        )}
      </div>

      {loading ? (
        <div
          className="bg-boss-surface border border-boss-border rounded-xl p-10 flex items-center justify-center gap-2 text-sm text-boss-text-muted"
          data-testid="tenant-users-loading"
        >
          <Loader2 size={16} className="animate-spin" />
          Loading users
        </div>
      ) : error ? (
        <div
          className="bg-boss-surface border border-boss-border rounded-xl p-10 text-center"
          data-testid="tenant-users-error"
        >
          <div className="w-14 h-14 rounded-xl bg-rose-50 dark:bg-rose-600/10 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-rose-600 dark:text-rose-400" />
          </div>
          <p className="text-sm text-boss-text-secondary mb-4">{error}</p>
          <button type="button" onClick={loadUsers} className="btn-secondary">
            Try again
          </button>
        </div>
      ) : users.length === 0 ? (
        <div
          className="bg-boss-surface border border-boss-border rounded-xl p-10 text-center"
          data-testid="tenant-users-empty"
        >
          <div className="w-14 h-14 rounded-xl bg-violet-50 dark:bg-violet-600/10 mx-auto mb-4 flex items-center justify-center">
            <Users className="w-7 h-7 text-violet-600 dark:text-violet-400" />
          </div>
          <h2 className="text-base font-semibold text-boss-text mb-1">
            No users yet
          </h2>
          <p className="text-sm text-boss-text-secondary max-w-md mx-auto">
            {canInvite
              ? 'Invite someone to give them sign-in access. They will see only your workspace once they accept.'
              : 'Nobody has been invited to this workspace yet.'}
          </p>
        </div>
      ) : (
        <TenantUserList
          users={users}
          onResend={canInvite ? handleResendInvite : undefined}
          onEditAppScope={canEditUsers ? setScopeTarget : undefined}
          resendingId={resendingUserId}
          appLabels={appLabels}
        />
      )}

      <EditAppScopeModal
        isOpen={!!scopeTarget}
        user={scopeTarget}
        apps={apps}
        callerScope={user?.app_scope}
        onClose={() => setScopeTarget(null)}
        onSave={handleSaveAppScope}
      />

      <InviteUserModal
        isOpen={inviteOpen}
        tenantId={tenantId}
        tenantName={user?.tenant_name || 'your workspace'}
        roleOptions={roleOptions}
        appOptions={apps}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => {
          setInviteOpen(false);
          loadUsers();
        }}
      />
    </div>
  );
}
